import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { TrendingUp, User, LogOut, Settings, Mail, ChevronDown, Briefcase, BarChart3, GraduationCap, MoreVertical, X, Home, Package, Target, BookOpen, Wrench, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import AuthModal from "./AuthModal";

const Navbar = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check for logged in user (check both key formats for compatibility)
    const storedUser = localStorage.getItem("user_data") || localStorage.getItem("user");
    const token = localStorage.getItem("user_token") || localStorage.getItem("token");
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Failed to parse user data");
      }
    }
  }, []);

  // Listen for auth changes
  useEffect(() => {
    const handleStorageChange = () => {
      const storedUser = localStorage.getItem("user_data") || localStorage.getItem("user");
      const token = localStorage.getItem("user_token") || localStorage.getItem("token");
      if (storedUser && token) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    // Also listen for custom auth event
    window.addEventListener("authChange", handleStorageChange);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("authChange", handleStorageChange);
    };
  }, []);

  const handleLogout = () => {
    // Clear all auth keys
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("user_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("remember_me");
    sessionStorage.removeItem("user_token");
    sessionStorage.removeItem("user_data");
    setUser(null);
    window.dispatchEvent(new Event("authChange"));
    window.location.href = "/";
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuth(false);
    window.dispatchEvent(new Event("authChange"));
  };

  const navLinks = [
    { name: "Home", path: "/", icon: Home },
    { name: "Products", path: "/products", icon: Package },
    { name: "Mission", path: "/mission", icon: Target },
    { name: "Blog", path: "/blog", icon: BookOpen },
    { name: "All Tools", path: "/tools", icon: Wrench },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="sticky top-0 z-50 glass border-b border-slate-200/50" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="nav-logo">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-navy-700 font-heading">
                Compoundr.
              </span>
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? "text-emerald-600"
                      : "text-slate-600 hover:text-navy-700"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button (3 dots) */}
            <div className="md:hidden flex items-center">
              <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="p-2"
                    data-testid="mobile-menu-btn"
                  >
                    <MoreVertical className="w-5 h-5 text-slate-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs text-slate-500 font-normal">Navigation</DropdownMenuLabel>
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <DropdownMenuItem key={link.path} asChild>
                        <Link 
                          to={link.path} 
                          className={`cursor-pointer flex items-center ${isActive(link.path) ? 'text-emerald-600' : ''}`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {link.name}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  
                  {user && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="cursor-pointer font-medium" onClick={() => setMobileMenuOpen(false)}>
                          <LayoutDashboard className="w-4 h-4 mr-2 text-navy-700" />
                          Your Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-slate-500 font-normal">Quick Access</DropdownMenuLabel>
                      <DropdownMenuItem asChild>
                        <Link to="/tools?tool=simulator" className="cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                          <Briefcase className="w-4 h-4 mr-2 text-emerald-600" />
                          My Portfolio
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/tools?tool=market-update" className="cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                          <BarChart3 className="w-4 h-4 mr-2 text-blue-600" />
                          Daily Market Update
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/tools?tool=learnhub" className="cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                          <GraduationCap className="w-4 h-4 mr-2 text-purple-600" />
                          Learn Hub
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        className="text-red-600 cursor-pointer"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Account Button */}
            <div className="flex items-center gap-3">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2 h-9 px-3"
                      data-testid="account-menu-btn"
                    >
                      <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <span className="hidden sm:inline text-sm font-medium text-slate-700 max-w-[100px] truncate">
                        {user.name || user.email?.split('@')[0]}
                      </span>
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b">
                      <p className="text-sm font-medium text-slate-800">{user.name || "User"}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    
                    {/* Dashboard Link */}
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer font-medium">
                        <LayoutDashboard className="w-4 h-4 mr-2 text-navy-700" />
                        Your Dashboard
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    
                    {/* Quick Links */}
                    <DropdownMenuLabel className="text-xs text-slate-500 font-normal">Quick Access</DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link to="/tools?tool=simulator" className="cursor-pointer">
                        <Briefcase className="w-4 h-4 mr-2 text-emerald-600" />
                        My Portfolio
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/tools?tool=market-update" className="cursor-pointer">
                        <BarChart3 className="w-4 h-4 mr-2 text-blue-600" />
                        Daily Market Update
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/tools?tool=learnhub" className="cursor-pointer">
                        <GraduationCap className="w-4 h-4 mr-2 text-purple-600" />
                        Learn Hub
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  onClick={() => setShowAuth(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 h-9 px-4 text-sm"
                  data-testid="sign-in-btn"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuth} 
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        required={true}
      />
    </>
  );
};

export default Navbar;
